import type {
  ComponentType,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement
} from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { Icon } from '@elfsight/lib-icon';
import { Typography } from '@elfsight/widget-ui';

export type ContactsItemProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    icon?: ComponentType;
    value: string | ReactElement;
    label?: string;
    href?: string;
    target?: '_self' | '_blank' | '_parent' | '_top' | string;
    showIcon?: boolean;
    showLabel?: boolean;
    lineHeight?: number;
    textStyle?: TextStyleValue;
    textColor?: string;
    iconColor?: string;
    withUnderline?: boolean;
    underlineAvailable?: boolean;
    cursor?: string;
  }
>;

export const ContactsItem = forwardRef<HTMLDivElement, ContactsItemProps>(
  function ContactsItem(
    {
      target = '_self',
      icon,
      label,
      value,
      href,
      showIcon,
      showLabel,
      lineHeight,
      textStyle = {
        fontSize: 15
      },
      textColor = '#1B1B1B',
      iconColor = 'rgba(17, 17, 17, 0.7)',
      withUnderline = true,
      underlineAvailable = true,
      cursor,
      className,
      ...forwardingProps
    },
    forwardingRef
  ) {
    if (!value) return null;

    return (
      <Item className={className} ref={forwardingRef}>
        {showIcon && icon && (
          <Icon size={20} component={icon} fill={iconColor} />
        )}
        <Typography
          {...forwardingProps}
          textStyle={textStyle}
          lineHeight={lineHeight}
          color={textColor}
        >
          {showLabel && label && <b>{label}: </b>}
          <UnderlineWrapper
            href={href}
            rel="nofollow noopener noreferrer"
            target={target}
            _withUnderline={withUnderline}
            _underlineAvailable={underlineAvailable}
            _cursor={cursor}
          >
            {value}
          </UnderlineWrapper>
        </Typography>
      </Item>
    );
  }
);

const UnderlineWrapper = styled.a<{
  _withUnderline: boolean;
  _underlineAvailable: boolean;
  _cursor?: string;
}>`
  cursor: ${({ _cursor }) => _cursor};
  color: inherit;

  text-decoration: ${({ _withUnderline, _underlineAvailable }) =>
    _withUnderline && _underlineAvailable ? 'underline' : 'auto'};

  &:hover {
    text-decoration: ${({ _underlineAvailable }) =>
      _underlineAvailable ? 'underline' : 'auto'};
  }
`;

const Item = styled.div`
  max-width: 100%;
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  text-decoration: none;
  outline: none;
`;
